import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import useApi, { USER_CREATE_DASHBOARD, USER_UPDATE_DASHBOARD } from 'hooks/useApi';
import RadioGroup from '@material-ui/core/RadioGroup';
import { RadioButton } from 'components/buttons/RadioButtons';
import { Message } from 'components/shared';
import Calendar from 'react-calendar';
import useNavigate from 'hooks/useNavigate';
import * as DASHBOARD_ACTIONS from 'hooks/useDashboard/actions';
import moment from 'moment';
import { dateTypeOptions, dynamicDateOptions } from '../content';
import StepControls from '../components/StepControls';

const DateSettingsStep = ({
  dispatchDashboard,
  dashboardData,
  setWhichPopup,
  setActiveStep,
  isUpdate
}) => {
  const [state, sendRequest] = useApi();
  const [updateState, sendUpdate] = useApi();
  const { data: response, isLoading, isError, error } = state;
  const {
    data: updateResponse,
    isLoading: isUpdateLoading,
    isError: isUpdateError,
    error: updateError
  } = updateState;

  const navigate = useNavigate();

  const { benchmarks, startDate, endDate, dateRangeTypeId } = dashboardData;
  const [dateTypeValue, setDateTypeValue] = useState(
    dateRangeTypeId === 0 ? 'customDate' : 'dynamicDate'
  );

  const patchStartDateVar = startDate || '2019-01-01';
  const patchEndDateVar = endDate || moment().format('YYYY-MM-DD');
  const [startDateState, setStartDateStateDate] = useState(
    new Date(startDate || patchStartDateVar)
  );
  const [endDateState, setEndDateStateDate] = useState(new Date(endDate || patchEndDateVar));
  const [dateRangeIdValue, setDateRangeIdValue] = useState(dateRangeTypeId ?? 1);

  const handleBack = () => setActiveStep(prev => prev - 1);

  const handleStartDateChange = newDate => {
    setStartDateStateDate(newDate);
  };

  const handleEndDateChange = newDate => {
    setEndDateStateDate(newDate);
  };
  const handleDateRangeIdChange = event => {
    setDateRangeIdValue(Number(event.target.value));
  };

  const handleDateTypeChange = event => {
    if (event.target.value === 'customDate') {
      setDateRangeIdValue(0);
    }
    setDateTypeValue(event.target.value);
  };

  const handleComplete = () => {
    const dateDate = {
      dateRangeType: Number(dateRangeIdValue),
      startReportingDate: moment(startDateState).format('YYYY-MM-DD'),
      endReportingDate: moment(endDateState).format('YYYY-MM-DD'),
      slots: 10
    };

    const reducedBenchmarks = benchmarks
      .filter(item => !!item.benchmarkId)
      .filter(benchmark => typeof benchmark.benchmarkId === 'number')
      .map(benchmark => benchmark.benchmarkId);

    const updateData = { ...dashboardData, ...dateDate, benchmarks: reducedBenchmarks };
    dispatchDashboard({
      type: DASHBOARD_ACTIONS.RESET_CREATE_DATA,
      payload: updateData
    });
    const params = USER_CREATE_DASHBOARD({ ...updateData });
    sendRequest(params);
  };

  const handleUpdate = () => {
    const dateDate = {
      dateRangeType: Number(dateRangeIdValue),
      startReportingDate: moment(startDateState).format('YYYY-MM-DD'),
      endReportingDate: moment(endDateState).format('YYYY-MM-DD'),
      slots: 10
    };

    const reducedBenchmarks = benchmarks
      .filter(item => !!item.benchmarkId)
      .filter(benchmark => typeof benchmark.benchmarkId === 'number')
      .map(benchmark => benchmark.benchmarkId);

    const masterObjectTypeId =
      dashboardData.masterObjectId === null ? null : dashboardData.masterObjectTypeId;
    const updateData = {
      ...dashboardData,
      ...dateDate,
      benchmarks: reducedBenchmarks,
      masterObjectTypeId
    };
    const params = USER_UPDATE_DASHBOARD({ ...updateData });
    sendUpdate(params);
  };

  useEffect(() => {
    if (isLoading || isError) return;

    if (response) {
      const { id } = response;
      setWhichPopup();
      navigate(`/dashboard/${id}`, null, { wasDashboardCreated: true });
    }
  }, [isError, isLoading, navigate, response, setWhichPopup]);

  useEffect(() => {
    if (isUpdateLoading || isUpdateError) return;

    if (updateResponse) {
      dispatchDashboard({
        type: DASHBOARD_ACTIONS.DASHBOARD_READY,
        payload: updateResponse
      });
      setWhichPopup();
    }
  }, [dispatchDashboard, isUpdateError, isUpdateLoading, setWhichPopup, updateResponse]);

  const renderError = () => {
    const errorMessage = error || updateError;

    return <Message type="error" text={errorMessage} />;
  };

  return (
    <>
      <Grid container direction="row" justify="center" alignItems="flex-start" spacing={2}>
        {(error || updateError) && renderError()}
        <Grid item xs={12}>
          <h3 style={{ textAlign: 'center' }}>Choose date type:</h3>
          <RadioGroup
            aria-label="quiz"
            name="quiz"
            value={dateTypeValue}
            onChange={handleDateTypeChange}
          >
            <Grid container direction="row" justify="center" alignItems="flex-start" spacing={1}>
              {dateTypeOptions &&
                dateTypeOptions.map(option => (
                  <Grid item xs="auto" key={option.title}>
                    <RadioButton title={option.title} value={option.value} />
                  </Grid>
                ))}
            </Grid>
          </RadioGroup>
          <Grid
            item
            container
            xs={12}
            direction="row"
            justify="center"
            alignItems="flex-start"
            spacing={1}
          >
            {dateTypeValue && dateTypeValue === 'customDate' && (
              <>
                <Grid item xs={12} md={6}>
                  <h4 style={{ textAlign: 'center' }}>Start Date</h4>
                  <Calendar onChange={handleStartDateChange} value={startDateState} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <h4 style={{ textAlign: 'center' }}>End Date</h4>
                  <Calendar onChange={handleEndDateChange} value={endDateState} />
                </Grid>
              </>
            )}
          </Grid>

          {dateTypeValue && dateTypeValue === 'dynamicDate' && (
            <Box pt={2}>
              <h4>Dynamic Date options:</h4>
              <RadioGroup
                aria-label="quiz"
                name="quiz"
                value={dateRangeIdValue}
                onChange={handleDateRangeIdChange}
              >
                <Grid
                  container
                  direction="row"
                  justify="flex-start"
                  alignItems="flex-start"
                  spacing={1}
                >
                  {dynamicDateOptions &&
                    dynamicDateOptions.map(option => (
                      <Grid item xs={6} key={option.title}>
                        <RadioButton title={option.title} value={option.value} />
                      </Grid>
                    ))}
                </Grid>
              </RadioGroup>
            </Box>
          )}
        </Grid>
      </Grid>
      <StepControls
        isLastStep
        handleBack={handleBack}
        handleNext={isUpdate ? handleUpdate : handleComplete}
      />
    </>
  );
};

DateSettingsStep.propTypes = {
  dashboardData: PropTypes.shape({
    description: PropTypes.string,
    name: PropTypes.string,
    endDate: PropTypes.string.isRequired,
    dateRangeTypeId: PropTypes.number.isRequired,
    startDate: PropTypes.string.isRequired,
    masterObjectId: PropTypes.number,
    masterObjectTypeId: PropTypes.number,
    endReportingDate: PropTypes.string,
    slots: PropTypes.string,
    startReportingDate: PropTypes.string,
    benchmarks: PropTypes.arrayOf(
      PropTypes.shape({
        benchmarkId: PropTypes.number,
        name: PropTypes.string
      })
    )
  }),
  dispatchDashboard: PropTypes.func,
  setWhichPopup: PropTypes.func,
  setActiveStep: PropTypes.func,
  isUpdate: PropTypes.bool
};

DateSettingsStep.defaultProps = {
  dashboardData: {},
  dispatchDashboard: null,
  setWhichPopup: null,
  setActiveStep: null,
  isUpdate: null
};

export default DateSettingsStep;
